/**
 * The Monitor inbox (PRD-0075 R-B5, ADR-0096): one row per rule and source — the rule, the source,
 * what it found there (the phrases, the entities, the pairs) with the hits and the date the case
 * learned it — triaged as a whole. A row of several alerts opens to each one, with its sample and
 * its own triage.
 *
 * Presentational: it is given the groups the feature reads and groups (`features/monitor`) and
 * the page's way of opening a document.
 */
import { BellRing, ChevronDown, FileText, PanelRightOpen } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import type { RuleSeverity } from '@/services/api/case-monitor'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/date'
import {
  AlertFindingLine,
  AlertObjectLabel,
  type AlertObjectRow,
  alertObjectText,
} from './AlertObject'
import { type AlertSortControl, AlertSortToggle } from './AlertSortToggle'
import {
  AlertTriageLine,
  AlertTriageMenu,
  type AlertTriageProps,
  type AlertTriageRow,
} from './AlertTriage'
import { type LoadView, MonitorSection } from './MonitorSection'
import { useRuleLabel } from './rule-name'
import { SeverityBadge } from './SeverityBadge'

const INBOX = 'cases.monitor.inbox'

/** The rule kinds the inbox has wording for; another one reads as a neutral "Rule". */
export const MONITOR_RULE_KINDS = [
  'occurrence',
  'phrase',
  'new_connection',
  'state_change',
] as const
export type MonitorRuleKind = (typeof MONITOR_RULE_KINDS)[number]

const RULE_LABELS: Record<MonitorRuleKind, string> = {
  occurrence: `${INBOX}.rules.occurrence`,
  phrase: `${INBOX}.rules.phrase`,
  new_connection: `${INBOX}.rules.new_connection`,
  state_change: `${INBOX}.rules.state_change`,
}

/** One alert as the inbox shows it: its object (an entity, a phrase, a pair) and what was found. */
export interface AlertInboxRow extends AlertObjectRow {
  id: string
  ruleId: string
  subjectType: string | null
  /** Null for a kind this build does not know (a newer server). */
  ruleKind: MonitorRuleKind | null
  /** The rule's name (ADR-0100); null for the default list, named in the reader's language. */
  ruleName: string | null
  /** The rule's severity; null for a value this build does not know. */
  severity: RuleSeverity | null
  documentId: string | null
  documentName: string | null
  /** When the case learned the source; ISO 8601. */
  learnedAt: string
  hits: number
  isNew: boolean
  /** What the team did with it (R-B5); left out, the row shows no triage. */
  triage?: AlertTriageRow
  /** Raised after the reader's previous visit to the Monitor (R-B6). */
  isUnread?: boolean
}

/** One rule in one source: the alerts it raised there, newest learned first. */
export interface AlertGroupRow {
  key: string
  ruleName: string | null
  ruleKind: MonitorRuleKind | null
  severity: RuleSeverity | null
  documentId: string | null
  documentName: string | null
  /** The newest of its alerts' learned dates; ISO 8601. */
  learnedAt: string
  hits: number
  newCount: number
  isUnread: boolean
  alerts: readonly AlertInboxRow[]
}

export interface AlertInboxProps {
  view: LoadView<AlertGroupRow>
  onOpenDocument: (documentId: string) => void
  onRetry: () => void
  /** The inbox's order: newest learned first or most severe first. */
  sort?: AlertSortControl
  /** Each row's triage line, and its menu for a reader who may triage. */
  triage?: AlertTriageProps
  /** Filters keep only some alerts: an empty inbox says none matches, not that none came. */
  filtered?: boolean
  /** The row whose detail is open; picking a row (or its *Detail* button) opens it. */
  selection?: { key: string | null; onSelect: (key: string) => void }
}

/** How many of a group's objects its row names before "+N". */
const OBJECTS_SHOWN = 8

function UnreadDot() {
  const { t } = useTranslation()
  return (
    <span
      role="img"
      aria-label={t('cases.monitor.triage.unread')}
      title={t('cases.monitor.triage.unread')}
      className="size-2 shrink-0 rounded-full bg-primary"
    />
  )
}

function Source({
  documentId,
  documentName,
  onOpenDocument,
}: {
  documentId: string | null
  documentName: string | null
  onOpenDocument: (id: string) => void
}) {
  const { t } = useTranslation()
  const name = documentName ?? t(`${INBOX}.unknownDocument`)
  if (!documentId) return <span className="truncate">{name}</span>
  return (
    <button
      type="button"
      onClick={() => onOpenDocument(documentId)}
      title={t(`${INBOX}.openDocument`, { document: name })}
      className="inline-flex min-w-0 items-center gap-1 truncate text-foreground/85 underline-offset-2 hover:text-foreground hover:underline"
    >
      <FileText aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{name}</span>
    </button>
  )
}

/** How a row ends: the hits, the learned date and, for a reader who may triage, the row's menu. */
function RowMeta({
  hits,
  learnedAt,
  alerts,
  name,
  triage,
  onDetail,
}: {
  hits: number
  learnedAt: string
  /** The alerts the menu triages. */
  alerts: readonly AlertInboxRow[]
  name: string
  triage: AlertTriageProps | undefined
  /** Opens the row's detail; left out, the row has none. */
  onDetail?: () => void
}) {
  const { t, i18n } = useTranslation()
  const learned = formatDate(learnedAt, i18n.language)
  return (
    <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground tabular-nums">
      <span>{t(`${INBOX}.hits`, { count: hits })}</span>
      <time dateTime={learnedAt} title={t(`${INBOX}.learnedOn`, { date: learned })}>
        {learned}
      </time>
      {onDetail ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={t('cases.monitor.detail.open', { name })}
          title={t('cases.monitor.detail.open', { name })}
          data-alert-detail-trigger
          onClick={onDetail}
        >
          <PanelRightOpen aria-hidden />
        </Button>
      ) : null}
      {triage?.actions ? (
        <AlertTriageMenu
          alerts={alerts.map(alert => ({ id: alert.id, state: alert.triage?.state ?? null }))}
          name={name}
          actions={triage.actions}
        />
      ) : null}
    </div>
  )
}

/** What the group's rule found in its source: each phrase, entity or pair once, as chips. */
function Objects({ alerts }: { alerts: readonly AlertInboxRow[] }) {
  const { t } = useTranslation()
  const rest = alerts.length - OBJECTS_SHOWN
  return (
    <ul className="flex flex-wrap gap-1">
      {alerts.slice(0, OBJECTS_SHOWN).map(alert => (
        <li key={alert.id}>
          <Badge variant="secondary" className="max-w-64 truncate">
            <AlertObjectLabel row={alert} />
          </Badge>
        </li>
      ))}
      {rest > 0 ? (
        <li>
          <Badge variant="outline">{t(`${INBOX}.moreObjects`, { count: rest })}</Badge>
        </li>
      ) : null}
    </ul>
  )
}

/** One alert of an opened group: its object, the sample, its triage and its own menu. */
function Finding({
  alert,
  triage,
}: {
  alert: AlertInboxRow
  triage: AlertTriageProps | undefined
}) {
  const { t } = useTranslation()
  return (
    <li className="flex flex-col gap-1 px-3 py-2 @xl:flex-row @xl:items-start @xl:gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-foreground">
          {alert.isUnread ? <UnreadDot /> : null}
          <span className="truncate">
            <AlertObjectLabel row={alert} />
          </span>
          {alert.subjectType ? <Badge variant="outline">{alert.subjectType}</Badge> : null}
          {alert.isNew ? <Badge variant="info">{t(`${INBOX}.new`)}</Badge> : null}
        </div>
        <AlertFindingLine finding={alert.finding} />
        {alert.triage && triage ? (
          <AlertTriageLine row={alert.triage} onOpenInvestigation={triage.onOpenInvestigation} />
        ) : null}
      </div>
      <RowMeta
        hits={alert.hits}
        learnedAt={alert.learnedAt}
        alerts={[alert]}
        name={alertObjectText(alert, t)}
        triage={triage}
      />
    </li>
  )
}

/** The rule, its wording, the severity, how many alerts nobody triaged yet, and the source. */
function GroupTitle({
  group,
  onOpenDocument,
}: {
  group: AlertGroupRow
  onOpenDocument: (id: string) => void
}) {
  const { t } = useTranslation()
  const ruleLabel = useRuleLabel()
  return (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {group.isUnread ? <UnreadDot /> : null}
        <span className="truncate text-sm font-medium text-foreground">
          {ruleLabel(group.ruleName)}
        </span>
        <span className="text-xs text-muted-foreground">
          {t(group.ruleKind ? RULE_LABELS[group.ruleKind] : `${INBOX}.rules.unknown`)}
        </span>
        {group.severity ? <SeverityBadge severity={group.severity} /> : null}
        {group.newCount > 0 ? (
          <Badge variant="info">{t(`${INBOX}.newCount`, { count: group.newCount })}</Badge>
        ) : null}
      </div>
      {/* A merge or an import is no document: the finding line says what happened. */}
      {group.documentId || group.documentName ? (
        <div className="flex min-w-0 text-xs text-muted-foreground">
          <Source
            documentId={group.documentId}
            documentName={group.documentName}
            onOpenDocument={onOpenDocument}
          />
        </div>
      ) : null}
    </>
  )
}

/** What a closed row says under its title: the chips, and for one alert its finding. */
function GroupSummary({
  group,
  triage,
}: {
  group: AlertGroupRow
  triage: AlertTriageProps | undefined
}) {
  const [only] = group.alerts
  // A row all triaged says what was done (with the first, as a group is triaged together).
  const triaged = group.newCount === 0 ? only.triage : undefined
  return (
    <>
      <Objects alerts={group.alerts} />
      {group.alerts.length === 1 ? <AlertFindingLine finding={only.finding} /> : null}
      {triaged && triage ? (
        <AlertTriageLine row={triaged} onOpenInvestigation={triage.onOpenInvestigation} />
      ) : null}
    </>
  )
}

/** A click on the row itself picks it; one on a control inside it (a link, a menu) does not. */
const onRow = (pick: () => void) => (event: MouseEvent<HTMLElement>) => {
  if ((event.target as HTMLElement).closest('button, a, [role="menuitem"]')) return
  event.currentTarget
    .querySelector<HTMLButtonElement>('[data-alert-detail-trigger]')
    ?.focus({ preventScroll: true })
  pick()
}

function GroupItem({
  group,
  onOpenDocument,
  triage,
  selection,
}: {
  group: AlertGroupRow
  onOpenDocument: (id: string) => void
  triage: AlertTriageProps | undefined
  selection: AlertInboxProps['selection']
}) {
  const { t } = useTranslation()
  const ruleLabel = useRuleLabel()
  const [only] = group.alerts
  const single = group.alerts.length === 1
  const selected = selection?.key === group.key
  const name = single
    ? alertObjectText(only, t)
    : [ruleLabel(group.ruleName), group.documentName].filter(Boolean).join(' · ')
  return (
    <Collapsible asChild disabled={single}>
      <li
        data-alert-group={group.key}
        aria-current={selected ? 'true' : undefined}
        onClick={selection ? onRow(() => selection.onSelect(group.key)) : undefined}
        className={cn(
          '@container rounded-md border bg-card',
          selection && 'cursor-pointer hover:border-border',
          selected ? 'border-primary/60 bg-primary/5' : 'border-border/60'
        )}
      >
        <div className="flex flex-col gap-1.5 px-3 py-2.5 @xl:flex-row @xl:items-start @xl:gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <GroupTitle group={group} onOpenDocument={onOpenDocument} />
            <GroupSummary group={group} triage={triage} />
            {single ? null : (
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className="group -ml-2 self-start text-muted-foreground"
                >
                  {t(`${INBOX}.findings`, { count: group.alerts.length })}
                  <ChevronDown
                    aria-hidden
                    className="transition-transform group-data-[state=open]:rotate-180"
                  />
                </Button>
              </CollapsibleTrigger>
            )}
          </div>
          <RowMeta
            hits={group.hits}
            learnedAt={group.learnedAt}
            alerts={group.alerts}
            name={name}
            triage={triage}
            onDetail={selection ? () => selection.onSelect(group.key) : undefined}
          />
        </div>
        <CollapsibleContent asChild>
          <ul className="divide-y divide-border/60 border-t border-border/60">
            {group.alerts.map(alert => (
              <Finding key={alert.id} alert={alert} triage={triage} />
            ))}
          </ul>
        </CollapsibleContent>
      </li>
    </Collapsible>
  )
}

export function AlertInbox({
  view,
  onOpenDocument,
  onRetry,
  sort,
  triage,
  filtered = false,
  selection,
}: AlertInboxProps) {
  const { t } = useTranslation()
  return (
    <MonitorSection
      heading={{ id: 'monitor-inbox-title', icon: BellRing, title: t(`${INBOX}.title`) }}
      action={sort ? <AlertSortToggle {...sort} /> : undefined}
      view={view}
      copy={{
        loading: t(`${INBOX}.loading`),
        failed: t(`${INBOX}.loadError`),
        empty: filtered ? t('cases.monitor.filters.noMatch') : t(`${INBOX}.empty`),
      }}
      onRetry={onRetry}
    >
      {groups => (
        <ul className="flex flex-col gap-2">
          {groups.map(group => (
            <GroupItem
              key={group.key}
              group={group}
              onOpenDocument={onOpenDocument}
              triage={triage}
              selection={selection}
            />
          ))}
        </ul>
      )}
    </MonitorSection>
  )
}
